import { useEffect, useRef, useState } from "react";
import { fetchNews, localizedNews, timeAgo } from "../lib/api";
import { useI18n } from "../i18n";
import NewsPreview from "./NewsPreview";
import { Newspaper } from "lucide-react";

export default function CoinNewsList({ symbol }) {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const listRef = useRef(null);
  const requestGeneration = useRef(0);

  useEffect(() => {
    let mounted = true;
    const generation = ++requestGeneration.current;
    setItems([]);
    setLoading(true);
    setSelected(null);
    if (listRef.current) listRef.current.scrollTop = 0;
    fetchNews(symbol)
      .then((d) => mounted && generation === requestGeneration.current && setItems(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => mounted && generation === requestGeneration.current && setLoading(false));
    const iv = setInterval(() => {
      const intervalGeneration = requestGeneration.current;
      fetchNews(symbol).then((d) => mounted && intervalGeneration === requestGeneration.current && setItems(Array.isArray(d) ? d : [])).catch(() => {});
    }, 120_000);
    return () => { mounted = false; clearInterval(iv); };
  }, [symbol]);

  return (
    <div className="border border-border/60 rounded-sm bg-[#09090B]" data-testid="coin-news-list">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <Newspaper className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} />
          <span className="font-display font-bold text-sm">{t("news")} · {symbol}</span>
        </div>
        <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">{items.length} {t("stories")}</span>
      </div>

      <div ref={listRef} className="max-h-[420px] overflow-y-auto divide-y divide-border/40">
        {loading && !items.length ? (
          <>
            <div className="shimmer h-14" />
            <div className="shimmer h-14" />
            <div className="shimmer h-14" />
          </>
        ) : !items.length ? (
          <div className="p-8 text-center text-zinc-500 text-sm">{t("news_building")}</div>
        ) : (
          items.map((raw) => { const n = localizedNews(raw, lang); return (
            <button
              key={n.id}
              onClick={() => setSelected(n)}
              data-testid={`coin-news-item-${symbol.toLowerCase()}-${n.id}`}
              className="w-full text-left flex gap-3 p-3 hover:bg-white/[0.03] transition-colors duration-150 group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase text-zinc-500 mb-0.5">
                  <span className="text-emerald-400">{n.source}</span>
                  <span>·</span>
                  <span>{timeAgo(n.published_at, lang)}</span>
                </div>
                <div className="text-xs text-white font-medium leading-snug group-hover:text-emerald-300 line-clamp-2">
                  {n.title}
                </div>
              </div>
            </button>
          ); })
        )}
      </div>

      <NewsPreview open={!!selected} onOpenChange={(o) => !o && setSelected(null)} item={selected} />
    </div>
  );
}
